<script setup lang="ts">
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledDropZone from "@/components/StyledDropZone.vue";
import StyledInput from "@/components/StyledInput.vue";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { computed, ref } from "vue";
import CoincidencesList from "../../../components/CoincidencesList.vue";
import ProviderFiltersSlideOver from "../../../components/ProviderFiltersSlideOver.vue";
import useSearchChains from "../chain/composables/useSearchChains";
import BrandList from "./components/BrandList.vue";
import useBrandForm from "./composables/useBrandForm";
import useBrands from "./composables/useBrands";
import useSearchBrands from "./composables/useSearchBrands";

const { data, isLoading, page, refetch, debouncedSetQuery, filters } =
  useSearchBrands({
    immediate: true,
    queryKey: "list-brands",
  });

const {
  data: chain,
  debouncedSetQuery: debouncedChains,
  isLoading: loadingChains,
} = useSearchChains({
  immediate: true,
  queryKey: "list-chains-brand",
  errorHandling: false,
});

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = newPage;
    refetch();
  }
};

const {
  form,
  handleEvaluation,
  handleCreate,
  clearState,
  actionModal,
  modalAction,
  isCoincidence,
  brands,
  createChainBrandLoading,
  createMediaMutationLoading,
} = useBrandForm();

const { deleteMutationAsync, restoreMutationAsync } = useBrands({
  immediate: false,
});

const pages = ref<Page[]>([
  {
    name: "Proveedores de hoteleria",
    to: {
      name: "hotel",
    },
  },
  {
    name: "Marcas",
    to: {
      name: "hotel.brands",
    },
  },
]);

const handleDelete = async (id: number) => {
  await deleteMutationAsync(id);
  refetch();
};

const handleRestore = async (id: number) => {
  await restoreMutationAsync(id);
  refetch();
};

// Slide over filters
const isFiltersOpen = ref(false);

const filtersActive = computed(() => {
  let count = 0;
  if (filters.active !== undefined) count++;
  if (filters.chain) count++;
  return count > 0 ? `(${count})` : "";
});

const resetFilters = () => {
  filters.orderBy = "name";
  filters.orderDirection = "asc";
  filters.active = undefined;
  filters.chain = undefined;
};
</script>

<template>
  <BreadCrumb :pages="pages" />

  <main class="mx-auto max-w-7xl px-4 py-6">
    <!-- Header -->
    <div class="mb-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Marcas</h1>
          <p class="mt-1 text-sm text-gray-500">
            Gestiona las marcas de las cadenas hoteleras
          </p>
        </div>

        <HasPermission
          :name-permission="'create-internal'"
          :entity-type="'App\\Models\\Brand'"
        >
          <StyledButton
            class="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md"
            @click="actionModal"
          >
            <span class="material-symbols-outlined text-xl">add</span>
            Nueva marca
          </StyledButton>
        </HasPermission>
      </div>

      <!-- Filters button -->
      <div class="mt-4 flex items-center gap-3">
        <button
          @click="isFiltersOpen = true"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow"
        >
          <span class="material-symbols-outlined text-lg">filter_alt</span>
          Filtros {{ filtersActive }}
        </button>

        <HasPermission
          :entity-type="'App\\Models\\Chain'"
          :name-permission="'viewAny-internal'"
        >
          <div class="w-64">
            <StyledCombobox
              v-model="filters.chain"
              placeholder="Filtrar por cadena"
              :options="[
                ...(chain?.data || []),
                {
                  id: '',
                  name: 'Sin cadena',
                  brandsCount: 0,
                  createdAt: '',
                  updatedAt: '',
                  isActive: false,
                  logo: '',
                },
                {
                  id: 0,
                  name: 'Todas las cadenas',
                  brandsCount: 0,
                  createdAt: '',
                  updatedAt: '',
                  isActive: false,
                  logo: '',
                },
              ]"
              :loading-options="loadingChains"
              :on-input-change="debouncedChains"
              :show-input="!filters.chain"
            >
              <template #default="{ option }">
                <span>{{ option.name }}</span>
              </template>

              <template #input-overlap>
                <template v-if="filters.chain">
                  <span>{{ filters.chain.name }}</span>
                  <button
                    class="material-symbols-outlined text-lg text-gray-400 hover:text-gray-600"
                    @click.stop="filters.chain = undefined"
                  >
                    close
                  </button>
                </template>
              </template>
            </StyledCombobox>
          </div>
        </HasPermission>
      </div>
    </div>

    <!-- List -->
    <BrandList
      :data="data?.data || []"
      :is-loading="isLoading"
      @action-modal="actionModal"
      @delete="handleDelete"
      @restore="handleRestore"
    >
      <template #pagination>
        <div class="mt-4 flex justify-end" v-if="data?.data?.length">
          <PaginateNav
            :page="page"
            :pagination="data"
            :data="data?.data || []"
            :handle-page="handlePage"
          />
        </div>
      </template>
    </BrandList>
  </main>

  <!-- Filters SlideOver -->
  <ProviderFiltersSlideOver
    :when="isFiltersOpen"
    :filters="filters"
    title="Filtros de marcas"
    icon="branding_watermark"
    icon-bg-color="bg-purple-100"
    icon-color="text-purple-600"
    button-color="bg-purple-600 hover:bg-purple-700"
    @close="isFiltersOpen = false"
    @search="debouncedSetQuery"
    @update:order-by="(v) => (filters.orderBy = v)"
    @update:order-direction="(v) => (filters.orderDirection = v)"
    @update:active="(v) => (filters.active = v)"
    @reset="resetFilters"
  />

  <!-- Modal create -->
  <ModalComponent
    :model-value="modalAction"
    @update:model-value="
      () => {
        clearState();
        actionModal();
      }
    "
  >
    <template #modalBody>
      <div class="p-6">
        <h2 class="mb-6 text-xl font-semibold text-gray-900">Crear nueva marca</h2>

        <form @submit.prevent="handleEvaluation" class="space-y-4">
          <HasPermission
            :name-permission="'create-medias-internal'"
            :entity-type="'App\\Models\\Brand'"
          >
            <StyledDropZone
              v-model="form.logo"
              class="!w-full"
              :accept-types="['image/png', 'image/jpg', 'image/jpeg']"
            />
          </HasPermission>

          <HasPermission
            :entity-type="'App\\Models\\Chain'"
            :name-permission="'viewAny-internal'"
          >
            <StyledCombobox
              v-model="form.chain"
              label="Cadena"
              placeholder="Buscar cadena"
              class="flex-1"
              :options="chain?.data || []"
              :loading-options="loadingChains"
              :on-input-change="debouncedChains"
              :show-input="!form.chain"
            >
              <template #default="{ option }">
                <span>{{ option.name }}</span>
              </template>

              <template #input-overlap>
                <template v-if="form.chain">
                  <span>{{ form.chain.name }}</span>
                  <button
                    class="material-symbols-outlined text-lg text-gray-400 hover:text-gray-600"
                    @click="form.chain = undefined"
                  >
                    close
                  </button>
                </template>
              </template>
            </StyledCombobox>
          </HasPermission>

          <StyledInput
            v-model="form.name"
            label="Nombre de la marca"
            placeholder="Ingresa el nombre de la marca"
            :is-required="true"
            :disabled="isCoincidence"
          />

          <StyledButton
            v-if="!isCoincidence"
            type="submit"
            class="w-full rounded-xl bg-blue-600 py-3 text-white hover:bg-blue-700"
            :disabled="createChainBrandLoading"
            :loading="createChainBrandLoading || createMediaMutationLoading"
          >
            Crear marca
          </StyledButton>

          <CoincidencesList
            :data="brands?.data || []"
            :is-coincidence="isCoincidence"
            :handle-action="handleCreate"
            :clear-state="clearState"
            :action-modal="actionModal"
            :loading-creating="createChainBrandLoading || createMediaMutationLoading"
          />
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
